/**
 * The PadPulse Promise — single source of truth for every customer-facing
 * string of the 30-day money-back guarantee.
 *
 * The Promise is a brand asset, not a pricing-page footnote: it renders on
 * the homepage hero, the pricing page, checkout, onboarding emails, the
 * in-app trust banner, billing settings, and the first invoice — always from
 * THIS object. Never re-type these strings per screen or per email.
 *
 * The enforced mechanic (see the guarantee-refund route): the FIRST paid
 * billing cycle carries a full 30-day money-back guarantee, one time per
 * account, refunded self-serve from billing settings after a short exit
 * survey. Guarantee copy must stay consistent with that mechanic — in
 * particular it must never promise "no forms": the exit survey is required.
 */

export const PADPULSE_PROMISE = {
  name: "The PadPulse Promise",
  headline: "If it doesn't pay for itself, we refund every dollar.",
  body:
    "Try it free for 14 days. Then if, in your first 30 days on a paid plan, " +
    "PadPulse isn't the easiest software decision you've made for your " +
    "portfolio — request a refund right from your billing page and we refund " +
    "100%. A 60-second exit survey, no retention call, no hoops.",
  fine:
    "Covers your first billing cycle. Account must be in active use during " +
    "that period. One guarantee per account. Add-on modules billed on your " +
    "first invoice carry the same guarantee, refunded as part of that invoice.",
  heroLine:
    "14 days free, then a 30-day money-back guarantee — if PadPulse doesn't " +
    "pay for itself, we refund every dollar.",
  checkoutLine:
    "No charge for 14 days. Your first paid month is covered by The PadPulse " +
    "Promise: 30 days, money back, guaranteed.",
  /**
   * Checkout copy when the account is NOT getting a fresh 14-day trial (the
   * trial has partly run or already ended), so "No charge for 14 days" would
   * be false.
   */
  paidCheckoutLine:
    "Your first paid month is covered by The PadPulse Promise: 30 days, " +
    "money back, guaranteed.",
  trialBadge: (daysLeft: number): string =>
    `${daysLeft} day${daysLeft === 1 ? "" : "s"} left in your free trial — then a 30-day money-back guarantee`,
  activeBadge: (throughDate: string): string =>
    `The PadPulse Promise is active — full refund available through ${throughDate}`,
  receiptFooter:
    "This invoice is covered by The PadPulse Promise — 30-day money-back guarantee.",
  /**
   * The required exit survey shown before a self-serve guarantee refund.
   * Keys match the columns on `offboarding_responses`.
   */
  offboardingQuestions: [
    { key: "liked", label: "What did you like about PadPulse?" },
    { key: "disliked", label: "What didn't work for you?" },
    { key: "wished", label: "What do you wish PadPulse had?" },
  ],
} as const;

export type OffboardingQuestionKey =
  (typeof PADPULSE_PROMISE.offboardingQuestions)[number]["key"];
